import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';
import { FileText, Upload, Download, Trash2, Search, Eye, EyeOff, Building, Users, Lock, Shield, Droplets, Settings, Link2 } from 'lucide-react';
import VisualizzatoreDocumento from '@/components/VisualizzatoreDocumento';
import RegistroPresaVisione from '@/components/RegistroPresaVisione';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document categories with Italian labels
const DOCUMENT_CATEGORIES = {
  BILANCIO_PREVENTIVO: 'Bilancio Preventivo',
  BILANCIO_CONSUNTIVO: 'Bilancio Consuntivo',
  RIPARTO_GENERALE: 'Riparto Generale',
  RIPARTO_UNITA: 'Riparto Unità',
  REGOLAMENTO_CONDOMINIALE: 'Regolamento',
  TABELLE_MILLESIMALI: 'Tabelle Millesimali',
  CONVOCAZIONE_ASSEMBLEA: 'Convocazione Assemblea',
  VERBALE_ASSEMBLEA: 'Verbale Assemblea',
  COMUNICAZIONE: 'Comunicazione',
  FATTURA_FORNITORE: 'Fattura Fornitore',
  CONTRATTO_FORNITORE: 'Contratto Fornitore',
  ESTRATTO_CONTO_BANCARIO: 'Estratto Conto Bancario',
  ESTRATTO_CONTO_PERSONALE: 'Estratto Conto Personale',
  ALTRO: 'Altro'
};

// Visibility options with Italian labels and icons
const VISIBILITY_OPTIONS = {
  RESIDENTS: { label: 'Tutti i Condomini', icon: Users, color: 'bg-green-100 text-green-800' },
  UNIT_ONLY: { label: 'Solo Unità', icon: Building, color: 'bg-blue-100 text-blue-800' },
  ADMIN_ONLY: { label: 'Solo Admin', icon: Lock, color: 'bg-red-100 text-red-800' },
  CONSULTANT_ONLY: { label: 'Solo Consulenti', icon: Shield, color: 'bg-purple-100 text-purple-800' },
  ALL: { label: 'Pubblico', icon: Eye, color: 'bg-gray-100 text-gray-800' }
};

export const Documents = () => {
  const { token, selectedCondominium, userRole } = useAuth();
  // Il condomino consulta i documenti ma non li scarica (le sue ricevute si): per lui l'anteprima e' il visualizzatore con filigrana
  const soloLettura = userRole === 'RESIDENT';
  const [docVisualizza, setDocVisualizza] = useState(null);
  const [registroAperto, setRegistroAperto] = useState(false);
  const navigate = useNavigate();
  const condoId = selectedCondominium?.condominium?.id;
  const [documents, setDocuments] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // anteprima: documento scelto, URL temporaneo del file in memoria, attesa
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [watermarkDialogOpen, setWatermarkDialogOpen] = useState(false);
  const [selectedDocForWatermark, setSelectedDocForWatermark] = useState(null);
  // «Chi lo vede» (richiesta titolare 08/09/2026): solo l'amministratore decide la visibilità
  const [visDoc, setVisDoc] = useState(null);
  const [visForm, setVisForm] = useState({ visibility: 'ADMIN_ONLY', unit_ids: [] });
  const [visSaving, setVisSaving] = useState(false);
  const [watermarkInfo, setWatermarkInfo] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    category: 'ALTRO',
    visibility: '',
    financial_year: new Date().getFullYear().toString(),
    unit_id: '',
    tags: ''
  });

  const fetchDocuments = useCallback(async () => {
    if (!selectedCondominium?.condominium?.id) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (visibilityFilter !== 'all') params.append('visibility', visibilityFilter);
      if (yearFilter !== 'all') params.append('financial_year', yearFilter);
      
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento documenti');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCondominium, searchQuery, categoryFilter, visibilityFilter, yearFilter]);

  const fetchUnits = useCallback(async () => {
    if (!selectedCondominium?.condominium?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUnits(data);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [token, selectedCondominium]);

  useEffect(() => {
    fetchDocuments();
    fetchUnits();
  }, [fetchDocuments, fetchUnits]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Seleziona un file');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title || uploadForm.file.name);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      if (uploadForm.visibility) formData.append('visibility', uploadForm.visibility);
      if (uploadForm.financial_year) formData.append('financial_year', uploadForm.financial_year);
      if (uploadForm.unit_id) formData.append('unit_id', uploadForm.unit_id);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);
      
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/upload`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (response.ok) {
        toast.success('Documento caricato con successo');
        setIsUploadOpen(false);
        setUploadForm({
          file: null,
          title: '',
          description: '',
          category: 'ALTRO',
          visibility: '',
          financial_year: new Date().getFullYear().toString(),
          unit_id: '',
          tags: ''
        });
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel caricamento');
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download avviato');
      } else {
        const error = await response.json();
        if (error.detail === 'PRESA_VISIONE_RICHIESTA') {
          toast.info('Prima di scaricare il verbale conferma la presa visione.');
          setDocVisualizza(doc);
          return;
        }
        toast.error(error.detail || 'Errore nel download');
      }
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  // Anteprima senza scaricare: il file sta dietro un endpoint protetto, quindi
  // non basta un link. Si scarica in memoria e lo si mostra da un URL
  // temporaneo, che viene revocato alla chiusura per non lasciare il file
  // appeso nel browser.
  const apriAnteprima = async (doc) => {
    if (soloLettura && doc.category !== 'QUIETANZA') { setDocVisualizza(doc); return; }
    const tipo = (doc.file_type || '').toLowerCase();
    const nome = (doc.file_name || '').toLowerCase();
    const anteprimabile = tipo.includes('pdf') || tipo.startsWith('image/') ||
      tipo.startsWith('text/') || /\.(pdf|png|jpe?g|gif|webp|txt|csv|md)$/.test(nome);
    if (!anteprimabile) {
      toast.info('Questo tipo di file non si può sfogliare qui: va scaricato.');
      return;
    }
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/${doc.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Errore nell\'apertura del documento');
        setPreviewDoc(null);
        return;
      }
      const blob = await response.blob();
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (error) {
      toast.error('Errore nell\'apertura del documento');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const chiudiAnteprima = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDoc(null);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.title}"?`)) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/${doc.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Documento eliminato');
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const canUpload = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';
  const canDelete = userRole === 'ADMIN';
  const canSetVisibility = userRole === 'ADMIN';

  const apriVisibilita = (doc) => {
    setVisDoc(doc);
    const vis = ['ADMIN_ONLY', 'RESIDENTS', 'UNIT_ONLY'].includes(doc.visibility) ? doc.visibility : (doc.visibility === 'ALL' ? 'RESIDENTS' : 'ADMIN_ONLY');
    const ids = (doc.unit_ids && doc.unit_ids.length ? doc.unit_ids : (doc.unit_id ? [doc.unit_id] : []));
    setVisForm({ visibility: vis, unit_ids: ids });
  };

  const salvaVisibilita = async () => {
    if (!visDoc) return;
    if (visForm.visibility === 'UNIT_ONLY' && visForm.unit_ids.length === 0) {
      toast.error('Scegli almeno un\'unità');
      return;
    }
    setVisSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/documents/${visDoc.id}/visibility`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: visForm.visibility, unit_ids: visForm.visibility === 'UNIT_ONLY' ? visForm.unit_ids : [] }),
      });
      if (res.ok) {
        toast.success('Visibilità aggiornata');
        setVisDoc(null);
        fetchDocuments();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nel salvataggio della visibilità');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setVisSaving(false);
    }
  };

  const unitaDelDoc = (doc) => {
    const ids = (doc.unit_ids && doc.unit_ids.length ? doc.unit_ids : (doc.unit_id ? [doc.unit_id] : []));
    return ids.map((id) => units.find((u) => u.id === id)?.unit_number || '?');
  };
  const canManageWatermark = userRole === 'ADMIN';

  // Fetch watermark info for a document
  const fetchWatermarkInfo = async (doc) => {
    try {
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/${doc.id}/watermark-info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const info = await response.json();
        setWatermarkInfo(info);
        setSelectedDocForWatermark(doc);
        setWatermarkDialogOpen(true);
      }
    } catch (error) {
      toast.error('Errore nel recupero informazioni watermark');
    }
  };

  // Update watermark settings
  const updateWatermarkSettings = async (forceWatermark, isSensitive) => {
    if (!selectedDocForWatermark) return;
    
    try {
      const formData = new FormData();
      formData.append('force_watermark', forceWatermark);
      formData.append('is_sensitive', isSensitive);
      
      const response = await fetch(
        `${API_URL}/api/condominiums/${selectedCondominium?.condominium?.id}/documents/${selectedDocForWatermark.id}/watermark-settings`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (response.ok) {
        toast.success('Impostazioni watermark aggiornate');
        fetchDocuments();
        // Refresh watermark info
        fetchWatermarkInfo({ ...selectedDocForWatermark, force_watermark: forceWatermark, is_sensitive: isSensitive });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  // Get unique years for filter
  const years = [...new Set(documents.map(d => d.financial_year).filter(Boolean))].sort().reverse();

  // Le ricevute (QUIETANZA) si dispongono per numero, che segue la data di pagamento (anno e progressivo), dalla piu' recente.
  // Gli altri documenti restano dove sono; le prove non valide non entrano nell'ordinamento.
  const numeroRicevuta = (d) => { const m = /Ricevuta n\. (\d{4})\/(\d+)/.exec(d.title || ''); return m ? [Number(m[1]), Number(m[2])] : [0, 0]; };
  const ordinaRicevute = (lista) => {
    const posti = []; const ricevute = [];
    lista.forEach((d, i) => { if (d.category === 'QUIETANZA' && !(d.title || '').includes('[PROVA')) { posti.push(i); ricevute.push(d); } });
    ricevute.sort((a, b) => { const x = numeroRicevuta(a); const y = numeroRicevuta(b); return (y[0] - x[0]) || (y[1] - x[1]); });
    const fuori = lista.slice(); posti.forEach((p, k) => { fuori[p] = ricevute[k]; });
    return fuori;
  };
  const documentiOrdinati = ordinaRicevute(documents);

  return (
    <div className="space-y-6">
      <PageHeader title="Documenti" subtitle="Archivio documenti del condominio" />
      <div className="flex flex-wrap gap-2 justify-end">
          {canUpload && (
            <Button variant="outline" onClick={() => setRegistroAperto(true)} data-testid="registro-presa-visione">
              Registro presa visione verbali
            </Button>
          )}
          <ExportButton
            condominiumId={condoId}
            exportType="documents-register"
            title="Esporta Registro"
            formats={['pdf']}
            filters={{ category: categoryFilter, year: yearFilter }}
            showArchive={true}
            token={token}
          />
          
          {canUpload && (
            <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Upload className="mr-2 h-4 w-4" />
                  Carica Documento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Carica Nuovo Documento</DialogTitle>
                  <DialogDescription>
                    Carica un documento nel registro del condominio
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleUpload} className="space-y-4">
                  <div className="space-y-2">
                    <Label>File *</Label>
                    <Input
                      type="file"
                      onChange={(e) => setUploadForm({
                        ...uploadForm,
                        file: e.target.files[0],
                        title: uploadForm.title || e.target.files[0]?.name || ''
                    })}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Titolo</Label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    placeholder="Nome documento"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <Select
                    value={uploadForm.category}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOCUMENT_CATEGORIES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Visibilità</Label>
                  <Select
                    value={uploadForm.visibility || "__default__"}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, visibility: v === "__default__" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Default per categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">Default per categoria</SelectItem>
                      {Object.entries(VISIBILITY_OPTIONS).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Anno</Label>
                    <Input
                      value={uploadForm.financial_year}
                      onChange={(e) => setUploadForm({ ...uploadForm, financial_year: e.target.value })}
                      placeholder="2024"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Unità</Label>
                    <Select
                      value={uploadForm.unit_id || "__none__"}
                      onValueChange={(v) => setUploadForm({ ...uploadForm, unit_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Nessuna" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna (Condominio)</SelectItem>
                        {units.map((unit) => (
                          <SelectItem key={unit.id} value={unit.id}>
                            {unit.unit_number} - {unit.unit_type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Tag (separati da virgola)</Label>
                  <Input
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                    placeholder="urgente, 2024, assemblea"
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsUploadOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" disabled={uploading}>
                    {uploading ? 'Caricamento...' : 'Carica'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca documenti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {Object.entries(DOCUMENT_CATEGORIES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Visibilità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le visibilità</SelectItem>
                {Object.entries(VISIBILITY_OPTIONS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documenti ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Caricamento...
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nessun documento trovato
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Visibilità</TableHead>
                  <TableHead>Watermark</TableHead>
                  <TableHead>Anno</TableHead>
                  <TableHead>Dimensione</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentiOrdinati.map((doc) => {
                  const visInfo = VISIBILITY_OPTIONS[doc.visibility] || VISIBILITY_OPTIONS.ALL;
                  const VisIcon = visInfo.icon;
                  const hasWatermark = doc.force_watermark || doc.is_sensitive || 
                    ['VISURA_CATASTALE', 'PLANIMETRIA_CATASTALE', 'ATTI_PROPRIETA', 'ALTRO_CATASTO', 
                     'ESTRATTO_CONTO_PERSONALE', 'SOLLECITO_PAGAMENTO', 'RIPARTO_UNITA', 'PIANO_RATE'].includes(doc.category);
                  
                  return (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">{doc.title}</div>
                            <div className="text-xs text-muted-foreground">{doc.file_name}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {DOCUMENT_CATEGORIES[doc.category] || doc.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => canSetVisibility && apriVisibilita(doc)}
                          title={canSetVisibility ? 'Cambia chi vede questo documento' : ''}
                          className={canSetVisibility ? 'cursor-pointer' : 'cursor-default'}
                          data-testid={`visibilita-${doc.id}`}
                        >
                          <Badge className={visInfo.color}>
                            <VisIcon className="h-3 w-3 mr-1" />
                            {visInfo.label}
                            {doc.visibility === 'UNIT_ONLY' && unitaDelDoc(doc).length > 0 && `: ${unitaDelDoc(doc).join(', ')}`}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell>
                        {hasWatermark ? (
                          <Badge className="bg-cyan-100 text-cyan-800">
                            <Droplets className="h-3 w-3 mr-1" />
                            {doc.force_watermark ? 'Forzato' : 'Auto'}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{doc.financial_year || '-'}</TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>{formatDate(doc.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => apriAnteprima(doc)}
                            title="Anteprima — apre il documento senza scaricarlo"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {(!soloLettura || doc.category === 'QUIETANZA' || doc.category === 'VERBALE_ASSEMBLEA') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(doc)}
                              title={hasWatermark ? "Download (con watermark)" : "Download"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {canSetVisibility && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => apriVisibilita(doc)}
                              title="Chi lo vede"
                              className="text-green-700"
                              data-testid={`chi-lo-vede-${doc.id}`}
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                          )}
                          {canManageWatermark && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => fetchWatermarkInfo(doc)}
                              title="Impostazioni Watermark"
                              className={hasWatermark ? "text-cyan-600" : ""}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          )}
                          {canManageWatermark && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/documents/${doc.id}/share`)}
                              title="Condivisione & Audit"
                              className="text-purple-600"
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(doc)}
                              className="text-red-600 hover:text-red-700"
                              title="Elimina"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Anteprima del documento — si sfoglia qui, senza salvarlo sul computer */}
      <Dialog open={!!previewDoc} onOpenChange={(aperto) => { if (!aperto) chiudiAnteprima(); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="truncate">{previewDoc?.title}</DialogTitle>
            <DialogDescription>
              {previewDoc?.file_name}
              {previewDoc?.file_size ? ` · ${formatFileSize(previewDoc.file_size)}` : ''}
              {' · anteprima, il file non viene salvato'}
            </DialogDescription>
          </DialogHeader>

          <div className="h-[70vh] w-full overflow-auto rounded-md border bg-muted/30">
            {previewLoading && (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Apertura del documento…
              </div>
            )}
            {!previewLoading && previewUrl && (
              (previewDoc?.file_type || '').startsWith('image/')
                ? <img src={previewUrl} alt={previewDoc?.title}
                       className="mx-auto max-h-full max-w-full object-contain" />
                : <iframe src={previewUrl} title={previewDoc?.title || 'documento'}
                          className="h-full w-full border-0" />
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <span className="text-xs text-muted-foreground">
              Se il documento non si vede, scaricalo: alcuni formati il browser non li apre.
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleDownload(previewDoc)}>
                <Download className="mr-2 h-4 w-4" />
                Scarica
              </Button>
              <Button onClick={chiudiAnteprima}>Chiudi</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canUpload && (
        <RegistroPresaVisione open={registroAperto} onOpenChange={setRegistroAperto} condominiumId={selectedCondominium?.condominium?.id} />
      )}

      {soloLettura && (
        <VisualizzatoreDocumento
          open={!!docVisualizza}
          onOpenChange={(aperto) => { if (!aperto) setDocVisualizza(null); }}
          condominiumId={selectedCondominium?.condominium?.id}
          documentId={docVisualizza?.id}
          titoloIniziale={docVisualizza?.title}
        />
      )}

      {/* Watermark Settings Dialog */}
      <Dialog open={watermarkDialogOpen} onOpenChange={setWatermarkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="h-5 w-5 text-cyan-600" />
              Impostazioni Watermark
            </DialogTitle>
            <DialogDescription>
              {selectedDocForWatermark?.title}
            </DialogDescription>
          </DialogHeader>
          
          {watermarkInfo && (
            <div className="space-y-4">
              {/* Watermark Status */}
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  {watermarkInfo.will_apply_watermark ? (
                    <>
                      <Droplets className="h-5 w-5 text-cyan-600" />
                      <span className="font-medium text-cyan-800">Watermark Attivo</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">Nessun Watermark</span>
                    </>
                  )}
                </div>
                {watermarkInfo.watermark_reason && (
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Motivo:</strong> {watermarkInfo.watermark_reason}
                  </p>
                )}
                {watermarkInfo.watermark_preview && (
                  <div className="text-xs font-mono bg-background p-2 rounded border">
                    {watermarkInfo.watermark_preview}
                  </div>
                )}
              </div>
              
              {/* Category Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Categoria:</span>
                  <div className="font-medium">{DOCUMENT_CATEGORIES[watermarkInfo.category] || watermarkInfo.category}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Watermark di categoria:</span>
                  <div className="font-medium">{watermarkInfo.category_requires_watermark ? 'Sì (automatico)' : 'No'}</div>
                </div>
              </div>
              
              {/* Admin Controls */}
              {canManageWatermark && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium">Impostazioni Admin</h4>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <div className="font-medium">Forza Watermark</div>
                      <div className="text-xs text-muted-foreground">
                        Applica watermark indipendentemente dalla categoria
                      </div>
                    </div>
                    <Button
                      variant={watermarkInfo.force_watermark ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateWatermarkSettings(!watermarkInfo.force_watermark, watermarkInfo.is_sensitive)}
                    >
                      {watermarkInfo.force_watermark ? 'Attivo' : 'Disattivo'}
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <div className="font-medium">Documento Sensibile</div>
                      <div className="text-xs text-muted-foreground">
                        Marca come sensibile per protezione extra
                      </div>
                    </div>
                    <Button
                      variant={watermarkInfo.is_sensitive ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateWatermarkSettings(watermarkInfo.force_watermark, !watermarkInfo.is_sensitive)}
                    >
                      {watermarkInfo.is_sensitive ? 'Attivo' : 'Disattivo'}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Warning */}
              {watermarkInfo.will_apply_watermark && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <strong>⚠️ Nota:</strong> {watermarkInfo.warning_it}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWatermarkDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chi lo vede: solo l'amministratore decide (richiesta titolare 08/09/2026) */}
      <Dialog open={!!visDoc} onOpenChange={(aperto) => { if (!aperto) setVisDoc(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Chi vede questo documento</DialogTitle>
            <DialogDescription className="truncate">{visDoc?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['ADMIN_ONLY', 'Solo amministrazione', 'lo vedete solo tu e il contabile'],
              ['RESIDENTS', 'Tutti i condòmini', 'chiunque abbia accesso al portale del condominio'],
              ['UNIT_ONLY', 'Solo alcune unità', 'solo i titolari delle unità che spunti qui sotto'],
            ].map(([v, lbl, descr]) => (
              <label key={v} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${visForm.visibility === v ? 'border-primary bg-primary/5' : ''}`}>
                <input type="radio" name="visibilita" className="mt-1" checked={visForm.visibility === v} onChange={() => setVisForm({ ...visForm, visibility: v })} data-testid={`vis-${v}`} />
                <span>
                  <span className="font-medium">{lbl}</span>
                  <span className="block text-xs text-muted-foreground">{descr}</span>
                </span>
              </label>
            ))}
            {visForm.visibility === 'UNIT_ONLY' && (
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Unità che lo vedono ({visForm.unit_ids.length})</span>
                  <span className="text-xs">
                    <button type="button" className="text-primary underline mr-2" onClick={() => setVisForm({ ...visForm, unit_ids: units.map((u) => u.id) })}>tutte</button>
                    <button type="button" className="text-primary underline" onClick={() => setVisForm({ ...visForm, unit_ids: [] })}>nessuna</button>
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1 max-h-56 overflow-y-auto">
                  {[...units].sort((a, b) => {
                    const ka = /^([A-Za-z]*)(\d+)/.exec(a.unit_number || '') || ['', a.unit_number, 0];
                    const kb = /^([A-Za-z]*)(\d+)/.exec(b.unit_number || '') || ['', b.unit_number, 0];
                    return String(ka[1]).localeCompare(String(kb[1])) || (parseInt(ka[2], 10) || 0) - (parseInt(kb[2], 10) || 0);
                  }).map((u) => (
                    <label key={u.id} className="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visForm.unit_ids.includes(u.id)}
                        onChange={(e) => setVisForm({
                          ...visForm,
                          unit_ids: e.target.checked ? [...visForm.unit_ids, u.id] : visForm.unit_ids.filter((x) => x !== u.id),
                        })}
                        data-testid={`vis-unita-${u.unit_number}`}
                      />
                      {u.unit_number}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Cambiare la visibilità non manda nessun avviso.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisDoc(null)}>Annulla</Button>
            <Button onClick={salvaVisibilita} disabled={visSaving} data-testid="vis-salva">{visSaving ? 'Salvo…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
